import { getCurrentTimeWindowStatus } from "../utils/get-current-time-window-status.js";
import { DateAdapter } from "@myphx-engineering/date-utils/client";

//#region src/hooks/use-maintenance-status.ts
/**
* This hook encapsulates the logic for determining the maintenance window status,
* including whether the maintenance banner should be shown and if the maintenance window
* is currently active.
*
* @param maintenanceWindow - The maintenance window configuration object (can be unknown from feature flags)
* @returns An object containing the following properties:
* - `showBanner` {boolean}: Indicates whether the maintenance banner should be shown.
* - `isMaintenanceActive` {boolean}: Indicates whether the maintenance is currently active.
* - `timeFrame` {Object}: The maintenance window object containing formatted `start` and `end` times.
* - `timeFrameStatus` {'within' | 'past' | 'future' | 'invalid'}: The status of the current time relative to the maintenance window.
* - `displaySameDate` {string | null}: Formatted date string if start and end are on same day (e.g., "1/25, 2:00 AM PST - 6:00 AM PST"), null otherwise
*/
const useMaintenanceStatus = (maintenanceWindow) => {
	const { show, timeFrame } = maintenanceWindow && typeof maintenanceWindow === "object" ? maintenanceWindow : { show: false };
	const timeFrameStatus = getCurrentTimeWindowStatus(timeFrame?.start, timeFrame?.end);
	const formatDateOptions = {
		month: "numeric",
		day: "numeric",
		hour: "numeric",
		minute: "numeric",
		timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		timeZoneName: "short"
	};
	let formattedStart = null;
	let formattedEnd = null;
	try {
		if (timeFrame?.start) {
			const startInstant = DateAdapter.fromISO(timeFrame.start);
			formattedStart = DateAdapter.formatToLocalizedDate(startInstant, formatDateOptions, "en-US");
		}
		if (timeFrame?.end) {
			const endInstant = DateAdapter.fromISO(timeFrame.end);
			formattedEnd = DateAdapter.formatToLocalizedDate(endInstant, formatDateOptions, "en-US");
		}
	} catch (error) {
		console.error("Error formatting maintenance window dates:", error);
	}
	let sameDay = false;
	if (timeFrame?.start && timeFrame?.end) try {
		const startInstant = DateAdapter.fromISO(timeFrame.start);
		const endInstant = DateAdapter.fromISO(timeFrame.end);
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const startDate = DateAdapter.getDateInTimeZone(startInstant, userTimeZone);
		const endDate = DateAdapter.getDateInTimeZone(endInstant, userTimeZone);
		sameDay = DateAdapter.isSameDay(startDate, endDate);
	} catch {
		console.log("Error checking same-day maintenance");
	}
	const shortFormatOptions = {
		hour: "numeric",
		minute: "numeric",
		timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		timeZoneName: "short"
	};
	let displaySameDate = null;
	if (sameDay && timeFrame?.end) try {
		const endInstant = DateAdapter.fromISO(timeFrame.end);
		const shortEnd = DateAdapter.formatToLocalizedDate(endInstant, shortFormatOptions, "en-US");
		displaySameDate = `${formattedStart} - ${shortEnd}`;
	} catch (error) {
		console.error("Error formatting same-day maintenance display:", error);
	}
	return {
		showBanner: show,
		isMaintenanceActive: show && timeFrameStatus === "within" || show && !timeFrame,
		timeFrame: {
			start: formattedStart,
			end: formattedEnd
		},
		timeFrameStatus,
		displaySameDate
	};
};

//#endregion
export { useMaintenanceStatus };